import { RuleModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import { Dispatch, SetStateAction, useState } from 'react';
import { useStore } from '../../state/zustand-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enableRules, RulesKeys } from '../../../api/rules-engine-api';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  RulesEngineRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface EnableRuleDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  selectedRules: RuleModel[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  onSuccess?: () => void;
}

export default function EnableRuleDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedRules,
  setSelectedRows,
  onSuccess,
}: EnableRuleDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const ruleIds = selectedRules.map((rule) => rule.id);
  const moreThanOneRule = selectedRules.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () => enableRules(ruleIds, changeLogMessage),
    onSuccess: () => {
      onSuccess?.();
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Automation rule${
          moreThanOneRule ? 's' : ''
        } enabled successfully!`,
      });
      setChangeLogMessage('');

      queryClient.setQueryData(
        [RulesKeys.RulesList],
        (oldData: RuleModel[]) => {
          if (!oldData) return [];
          return oldData.map((rule) => {
            if (ruleIds.includes(rule.id)) {
              return {
                ...rule,
                basicInfo: {
                  ...rule.basicInfo,
                  status: 'Enabled',
                },
              };
            }
            return rule;
          });
        }
      );
      handleClose();
      setSelectedRows([]);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue enabling the selected automation rule${
          moreThanOneRule ? 's' : ''
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.rules_engine.enable_rule, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.rulesEngine,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        RulesEngineRoles.ChangeRuleStatus.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Enable Automation Rule${moreThanOneRule ? 's' : ''}`}
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            {moreThanOneRule
              ? `Do you wish to enable the ${selectedRules.length} selected automation rules?`
              : `Do you wish to enable the automation rule: "${selectedRules[0].basicInfo.ruleName}"?`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Enable',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
